import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Button, Modal, Input, Switch, InputNumber, Toast, Spin, Select, Checkbox, Pagination } from '@douyinfe/semi-ui';
import { IconSearch, IconClose } from '@douyinfe/semi-icons';
import { API, apiErrMsg, clipboardAvailable, pageVisible } from '../helpers';
import { summarizeLive, fmtTps, fmtElapsed, liveStatusText } from '../helpers/speed';
import { navigate } from '../router';
import { usePagedRows } from '../helpers/usePagedRows';

function maskKey(k) {
  if (!k) return '';
  return k.slice(0, 8) + '...' + k.slice(-8);
}

// 复制到剪贴板。
// ⚠️ 非 HTTPS(且非 localhost)时 navigator.clipboard 整个不存在,
//   直接 navigator.clipboard.writeText(...) 是**同步抛 TypeError**(不是 reject),
//   漏出去会被全局错误处理弹成红色 JS 异常框。先判可用性,不行就提示手动复制。
function copyKey(text, msg = '已复制') {
  if (!clipboardAvailable()) {
    Toast.error('当前环境不支持复制(需要 HTTPS),请手动选中复制');
    return false;
  }
  navigator.clipboard.writeText(text).then(
    () => Toast.success(msg),
    () => Toast.error('复制失败,请手动选中复制')
  );
  return true;
}

// 速度单元格:显示**此刻正在调用**的卡的实时输出速度(t/s)。
//
// 只看在飞的请求:没在飞就显示「—」。
// 于是这一列直接就是「哪些卡正在被调用」的名单 —— 不用再去对日志、猜历史。
//
// ⚠️ 与「网关总览 → 在飞请求」表的关系(用户会拿两边的数字互相对照):
//   两边是**同一个量**:tps = 客户端实际收到的输出速度(已含降速效果),
//   数据同源、口径共用(server/inflight-view.js + src/helpers/speed.js),
//   同一条在飞请求在两边是同一个数字。
//   差别只在**呈现方式**:总览是「每条在飞请求占一行」逐条列出;
//   本表一格只放一个数,显示**最快的那条**(代表这张卡此刻的能力上限),
//   其余的在后面用 (+N) 标出,鼠标悬停能逐条看到(带在飞编号,可与总览逐行对上)。
//
// 限速比例(5h 用量决定的百分比)不放进表格,只留在 tooltip 里:
//   百分比回答"被限了多狠",t/s 回答"到底多快",两个问题别混在一列。
//
// ⚠️ 「没有速度」有两种完全不同的原因,不能都写成「非流式」:
//   1) 非流式请求 —— 本来就没有逐段输出,测不出速率;
//   2) 流式请求**还没出第一个字**(在等上游预填充 / 深度思考)——
//      长上下文请求这一段能等 20~80 秒,恰恰是最该被看见的状态。
//   早期版本把两者都标成「非流式」,会让人以为这张卡在跑批量任务。
const SpeedCell = ({ th, live }) => {
  // 速度口径与「网关总览 → 在飞请求」表共用(src/helpers/speed.js):
  // 两边是同一个量、同一套格式化规则,数字能互相对照。
  const { queued, running, withTps, pending, best, others, thinking } = summarizeLive(live);
  if (!running.length && !queued.length) {
    return React.createElement('span', {
      style: { color: '#c9cdd4' },
      title: '当前没有请求在飞',
    }, '—');
  }
  // tps > 0 才算数:tps=null(还没出第一帧)和 tps=0(此刻确实没收到内容)都不是速率
  // (summarizeLive 里已按这个口径拆好:running / queued / withTps / pending)
  const pct = Math.round((th?.progress || 0) * 100);
  // 颜色按限速比例:没被限速=绿,限得越狠越红(提示色,数字本身是速度)
  const speedPct = th?.enabled ? Math.round((th.factor || 0) * 100) : 100;
  const baseColor = speedPct >= 90 ? '#00b42a' : (speedPct >= 60 ? '#ff7d00' : '#f56565');

  const head = '正在调用 ' + running.length + ' 个请求'
    + (queued.length ? '(另有 ' + queued.length + ' 个在排队等并发槽)' : '');
  const tail = [
    th?.enabled
      ? '当前限速比例 ' + speedPct + '%(保底 ' + (th.minPct || 0) + '%)'
      : '未限速(满速)',
    '5h 已用 ' + (th?.used || 0) + ' / ' + (th?.limit || '∞') + '(' + pct + '%)',
    'token 数按字符估算,误差约 ±20%',
  ];

  if (!withTps.length) {
    // 一个速率都测不出来:先分清是「非流式」还是「还在等上游出第一个字」
    const allNonStream = pending.length === 0;
    return React.createElement('span', {
      style: { color: allNonStream ? '#86909c' : '#ff7d00', whiteSpace: 'nowrap' },
      title: allNonStream
        ? [head, '都是非流式请求,没有逐段输出,测不出实时速率'].concat(tail).join('\n')
        : [head, '流式请求还没出第一个字(在等上游预填充 / 深度思考),暂时测不出速率',
           '长上下文 + 深度思考时这一段可能等 20~80 秒'].concat(tail).join('\n'),
    }, allNonStream ? '非流式' : '等待上游');
  }

  // 思考中的请求:数字用紫色提示 —— 「它在想,不是卡了,也不是上游变慢」
  const color = thinking.length ? '#7c5cff' : baseColor;
  const lines = [head];
  // 逐条列出这张卡每一条在飞请求的速度 —— 与「网关总览」的在飞表逐行对应
  // (带 #在飞编号,可直接对上行)
  withTps.slice().sort((a, b) => b.tps - a.tps).forEach((x, i) => {
    lines.push(
      (i === 0 ? '最快 ' : '另有 ') + fmtTps(x.tps)
      + (x.upstream_tps ? '(上游 ' + x.upstream_tps.toFixed(1) + ' t/s)' : '')
      + ' · ' + (x.model || '—')
      + ' · ' + liveStatusText(x)
      + ' · 已用时 ' + fmtElapsed(x.elapsed_ms)
      + (x.reasoning_tokens ? ' · 思考 ' + x.reasoning_tokens + ' token' : '')
      + (x.id ? ' · 在飞 #' + x.id : '')
    );
  });
  if (thinking.length) {
    lines.push('其中 ' + thinking.length + ' 个正在深度思考(已产出思考 token,首字还没出来)');
  }
  if (pending.length) {
    lines.push('另有 ' + pending.length + ' 个流式请求还在等上游出第一个字');
  }
  if (others.length) {
    lines.push('本列只显示最快的一条;其余 ' + others.length + ' 条见上面逐条列表(与网关总览的在飞表一一对应)');
  }
  lines.push(...tail);
  return React.createElement('span', {
    style: { color, whiteSpace: 'nowrap' },
    title: lines.join('\n'),
  }, fmtTps(best.tps)
    + (others.length
      ? React.createElement('span', { style: { color: '#86909c', marginLeft: 4 } }, '(+' + others.length + ')')
      : null));
};

function formatQuota(n) {
  if (!n) return '0';
  return Number(n).toLocaleString();
}

// 状态文案 → 颜色。与表格「状态」列保持一致,卡片视图复用。
const STATUS_COLORS = {
  '待售': '#86909c',
  '待激活': '#1d7dfa', // 蓝色:已售等待买家首次调用
  '使用中': '#00b42a',
  '已禁用': '#86909c',
  '已过期': '#f56565',
  '已耗尽': '#f56565',
};

const EMPTY_FORM = { count: 1, key: '', remain_quota: 0, unlimited: false, remark: '' };

// 开关状态持久化到 localStorage。
// 为什么要存:这两个开关是"我的使用偏好"(自动刷新开不开、要不要隐藏过期),
// 每次进页面都要重设一遍很烦 —— 刷新一下浏览器 / 重新登录后就丢,等于白设。
// 用法:useState(() => readFlag(key, 默认值)) 读,setter 里 writeFlag 写。
const FLAG_KEYS = {
  autoRefresh: 'meow.tokens.autoRefresh',
  hideExpired: 'meow.tokens.hideExpired',
};
function readFlag(key, defVal) {
  try {
    const v = window.localStorage.getItem(key);
    if (v === null) return defVal;
    return v === '1';
  } catch {
    return defVal; // 隐私模式 / localStorage 不可用:退回默认值,不影响功能
  }
}
function writeFlag(key, val) {
  try {
    window.localStorage.setItem(key, val ? '1' : '0');
  } catch { /* 忽略:存不了也只是下次不记住,不影响功能 */ }
}

// 筛选条件(售出状态 / 套餐)也存在 sessionStorage 里。
// 为什么要存:进详情页再「返回列表」时,组件会重新挂载,useState 里的筛选会被重置成「全部」,
// 管理员刚筛出来的结果凭空消失、表格还重新全量加载一次。存 sessionStorage 后返回即还原。
// 用 sessionStorage 而不是 localStorage:筛选属于"当前这一波工作",不该跨天保留。
const FILTER_KEY = 'meow.tokens.filters';
function readFilters() {
  try {
    const raw = window.sessionStorage.getItem(FILTER_KEY);
    if (!raw) return { sold: '', plan: '', search: '' };
    const o = JSON.parse(raw);
    return {
      sold: typeof o.sold === 'string' ? o.sold : '',
      plan: typeof o.plan === 'string' ? o.plan : '',
      search: typeof o.search === 'string' ? o.search : '',
    };
  } catch {
    return { sold: '', plan: '', search: '' };
  }
}
function writeFilters(v) {
  try {
    window.sessionStorage.setItem(FILTER_KEY, JSON.stringify(v));
  } catch { /* 忽略 */ }
}

const AdminTokens = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // 隐藏过期开关:默认关(全部显示)。打开后列表不显示状态为「已过期」的卡,
  // 只影响显示,不动数据 —— 关掉开关就能再看到。状态记在 localStorage,刷新页面不丢。
  const [hideExpired, setHideExpiredState] = useState(() => readFlag(FLAG_KEYS.hideExpired, false));
  const setHideExpired = (v) => { writeFlag(FLAG_KEYS.hideExpired, v); setHideExpiredState(v); };

  // 售出状态 / 套餐 / 搜索关键词:三个筛选条件共用一份 sessionStorage 快照,
  // 任何一个变了就把整份重写回去(进详情页再返回时能还原当前筛选)。
  const [soldFilter, setSoldFilterRaw] = useState(() => readFilters().sold); // ''=全部, '0'=未售出, '1'=已售出
  const [planFilter, setPlanFilterRaw] = useState(() => readFilters().plan); // ''=全部, '0'=未关联套餐, 正数=指定套餐 id
  const [search, setSearchRaw] = useState(() => readFilters().search); // 关键词搜索(前端过滤)

  const persistFilters = (next) => {
    writeFilters({ sold: next.sold, plan: next.plan, search: next.search });
  };
  const setSoldFilter = (v) => {
    setSoldFilterRaw(v);
    persistFilters({ sold: v, plan: planFilter, search });
  };
  const setPlanFilter = (v) => {
    setPlanFilterRaw(v);
    persistFilters({ sold: soldFilter, plan: v, search });
  };
  const setSearch = (v) => {
    setSearchRaw(v);
    persistFilters({ sold: soldFilter, plan: planFilter, search: v });
    // 换了关键词,当前页码很可能越界 → 回到第一页
    setMobilePage(1);
    setTablePage(1);
  };

  // 搜索关键词归一化:小写 + 去首尾空格。
  // 命中范围:Key(片段)/ 备注 / ID(数字)/ 归属用户名 / 售出对象 / 套餐名。
  // 为什么放前端过滤:令牌总量很小(几十张卡),后端列表接口本来就把全量返回了,
  // 前端过滤零延迟、也不会给远程 PG 再加一条 WHERE(那条链路的大报文会被中间设备吞掉)。
  const searchKey = (search || '').trim().toLowerCase();
  const matchesSearch = (r) => {
    if (!searchKey) return true;
    // 纯数字 → 当作 ID 精确/前缀匹配
    if (/^\d+$/.test(searchKey)) {
      if (String(r.id) === searchKey) return true;
    }
    const hay = [
      r.key, r.remark, r.owner, r.sold_to, r.plan_name,
      r.status_text, r.name,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(searchKey);
  };

  const visibleList = useMemo(
    () => {
      let out = hideExpired ? list.filter((r) => r.status_text !== '已过期') : list;
      if (searchKey) out = out.filter(matchesSearch);
      return out;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, hideExpired, searchKey]
  );
  const hiddenExpiredCount = list.length - (hideExpired ? list.filter((r) => r.status_text !== '已过期').length : list.length);
  // 搜索命中数提示:告诉管理员"当前筛出 N 条 / 共 M 条",避免"搜不到"被误认为是数据没了
  const searchHitCount = searchKey ? visibleList.length : 0;

  // 桌面端表格分页:自己持有 currentPage。
  // Semi Table 在 dataSource 引用变化(本页每 5 秒自动刷新一次)时会重置 currentPage,
  // 翻到第 2 页会被弹回第 1 页;受控分页 + 自己切片可避免。
  const TABLE_PAGE_SIZE = 20;
  const { page: tablePage, pagedList, setPage: setTablePage } = usePagedRows(visibleList, TABLE_PAGE_SIZE);

  // 窄屏(手机)用卡片视图替代表格:表格 11 列 + fixed 操作列在手机上会互相遮挡
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobilePage, setMobilePage] = useState(1);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 套餐列表(用于生成令牌时选择套餐模板)
  const [plans, setPlans] = useState([]);

  // 弹窗两种模式:create / edit
  const [mode, setMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  // 编辑前的原始 Key(用于判断"Key 到底有没有被改过")
  const origKeyRef = useRef('');
  const [saving, setSaving] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);

  // 行内标记/取消标记售出小弹窗: { open, row, targetStatus: 0|1, soldTo, saving }
  const [markDialog, setMarkDialog] = useState(null);
  // 批量删除确认弹窗: { open, ids, saving }
  const [batchDeleteDialog, setBatchDeleteDialog] = useState(null);
  // 续期弹窗: { open, row, months, saving }
  const [renewDialog, setRenewDialog] = useState(null);

  // 请求序号:只接受最后一次请求的响应。
  // 为什么需要:切筛选 / 5 秒轮询 / 手动刷新可能同时在飞,慢的旧请求后到会覆盖新结果,
  // 表现为"刚切到未售出,列表又变回全量"。项目里 AdminChannels 用的就是这个范式。
  const reqSeq = useRef(0);

  const load = (silent) => {
    if (!silent) setLoading(true); // 静默轮询不转圈,避免整表闪烁
    const seq = ++reqSeq.current;
    const params = [];
    if (soldFilter !== '') params.push('sold_status=' + soldFilter);
    if (planFilter !== '') params.push('plan_id=' + planFilter);
    const qs = params.length ? '?' + params.join('&') : '';
    API.get('/api/admin/tokens' + qs)
      .then((r) => {
        if (seq !== reqSeq.current) return; // 已有更新的请求在飞,丢弃这次过期响应
        setList(r.data.data || []);
      })
      // 这个接口 5 秒自动刷新一次,漏掉 catch 就会因偶发网络抖动弹红框。
      // 静默轮询失败只记控制台(否则一次断网会连着弹 5 秒一个的提示),
      // 手动刷新才弹 Toast。
      .catch((e) => {
        if (silent) { console.warn('[AdminTokens] 静默刷新失败:', apiErrMsg(e)); return; }
        Toast.error(apiErrMsg(e));
      })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  };
  useEffect(load, []);

  // 套餐列表:只加载一次。
  // 以前它被塞在 load() 里,而 load() 每 5 秒轮询一次 → 令牌页开着时每 5 秒多打一个
  // /api/admin/plans,套餐变动时还会让下拉框选项跳动。套餐在别的页面管理,
  // 本页挂载时拉一次就够。
  useEffect(() => {
    API.get('/api/admin/plans')
      .then((r) => setPlans(r.data.data || []))
      .catch(() => {});
  }, []);

  // 筛选条件变化:立即重载一次。
  // 以前页面上有个「刷新」按钮可以手动刷,现在按钮删了(改成自动刷新开关),
  // 再等 5 秒一轮就变成"切完筛选干等",所以这里立刻拉一次。
  const firstFilterRun = useRef(true);
  useEffect(() => {
    if (firstFilterRun.current) { firstFilterRun.current = false; return; }
    load(true);
  }, [soldFilter, planFilter]);

  // 自动刷新:默认开,每 5 秒静默拉一次(不转圈、不动分页与勾选)。
  // 任一弹窗打开时暂停,避免编辑表单期间后台数据跳动;筛选变化时立即重载
  // (顺带修复了此前切换筛选不自动刷新、必须手点刷新按钮的问题)。
  // 开关状态记在 localStorage,刷新页面/重新登录后保持上次选择。
  const [autoRefresh, setAutoRefreshState] = useState(() => readFlag(FLAG_KEYS.autoRefresh, true));
  const setAutoRefresh = (v) => { writeFlag(FLAG_KEYS.autoRefresh, v); setAutoRefreshState(v); };
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const dialogOpen = modalOpen || markDialog?.open || batchDeleteDialog?.open || renewDialog?.open;
    if (dialogOpen) return undefined;
    const t = setInterval(() => {
      if (!pageVisible()) return; // 页面看不见(切标签页/最小化)就别白打接口
      load(true);
    }, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, modalOpen, markDialog, batchDeleteDialog, renewDialog, soldFilter, planFilter]);

  const openNew = async () => {
    setMode('create');
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setNewKeyResult(null);
    setModalOpen(true);
    // 选默认套餐:优先用管理员在套餐页"设为默认"指定的;若该套餐已下架/被删,回退到第一个 enabled
    let chosen = null;
    try {
      const r = await API.get('/api/admin/settings');
      const prefId = parseInt(r.data.data?.default_token_plan_id || '0', 10) || 0;
      if (prefId > 0) {
        const pref = plans.find((p) => p.id === prefId);
        if (pref && pref.enabled) chosen = pref;
      }
    } catch { /* 忽略,继续走回退 */ }
    if (!chosen) chosen = plans.find((p) => p.enabled) || null;
    setSelectedPlanId(chosen ? String(chosen.id) : '');
    if (chosen) {
      setForm((prev) => ({
        ...prev,
        remain_quota: chosen.total_calls || 0,
        unlimited: false,
      }));
    }
  };

  // 选择套餐 → 自动填充余额(其他字段从套餐读)
  const applyPlan = (planId) => {
    setSelectedPlanId(planId);
    if (!planId) return;
    const p = plans.find((x) => String(x.id) === String(planId));
    if (!p) return;
    setForm((prev) => ({
      ...prev,
      remain_quota: p.total_calls || 0,
      unlimited: false,
    }));
  };

  const openEdit = (row) => {
    setMode('edit');
    setEditingId(row.id);
    setForm({
      key: row.key || '',
      remain_quota: row.remain_quota ?? 0,
      unlimited: !!row.unlimited,
      remark: row.remark || '',
    });
    // 记下原始 Key:保存时**只在 Key 真的被改动过**才提交 key 字段。
    // 否则改个备注也会把 key 原样发回去,后端就会拿"至少 16 位/唯一"去校验它 ——
    // 存量里如果有短 Key 的老令牌,连改备注都保存不了。
    origKeyRef.current = row.key || '';
    setSelectedPlanId(row.plan_id ? String(row.plan_id) : '');
    setNewKeyResult(null);
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (mode === 'create') {
        const r = await API.post('/api/admin/tokens', {
          ...form,
          count: Number(form.count) || 1,
          remark: form.remark?.trim() || '',
          plan_id: selectedPlanId ? Number(selectedPlanId) : 0,
        });
        setNewKeyResult(r.data.data);
        if (r.data.data.count > 1) Toast.success('已批量生成 ' + r.data.data.count + ' 个 Key');
        else Toast.success('已创建,完整 Key 只显示一次');
      } else {
        const payload = {
          remark: form.remark?.trim() || '',
          remain_quota: form.unlimited ? -1 : Number(form.remain_quota) || 0,
          unlimited: form.unlimited ? 1 : 0,
          plan_id: selectedPlanId ? Number(selectedPlanId) : 0,
        };
        // Key 改动过才提交(见 openEdit 的注释)
        const k = (form.key || '').trim();
        if (k !== origKeyRef.current) payload.key = k;
        await API.put(`/api/admin/tokens/${editingId}`, payload);
        Toast.success('已保存');
        setModalOpen(false);
      }
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === 1 ? 2 : 1;
    try {
      await API.put('/api/admin/tokens/' + row.id, { status: next });
      Toast.success(next === 1 ? '已启用' : '已禁用');
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '操作失败');
    }
  };

  const remove = (row) => {
    Modal.confirm({
      title: '删除令牌?',
      content: '将删除令牌 sk-' + maskKey(row.key) + ',此操作不可恢复',
      onOk: async () => {
        try {
          await API.delete('/api/admin/tokens/' + row.id);
          Toast.success('已删除');
          load();
        } catch (e) {
          Toast.error(apiErrMsg(e));
        }
      },
    });
  };

  // 续期:延长有效期 + 本计费周期次数清零重算
  const doRenew = async () => {
    const { row, months } = renewDialog;
    setRenewDialog((d) => ({ ...d, saving: true }));
    try {
      const r = await API.post(`/api/admin/tokens/${row.id}/renew`, { months });
      Toast.success(`已续期 ${months} 个月,当前周期用量不变`);
      setRenewDialog(null);
      load();
    } catch (e) {
      Toast.error(e.response?.data?.message || '续期失败');
      setRenewDialog((d) => ({ ...d, saving: false }));
    }
  };

  const openRenew = (row) => setRenewDialog({ open: true, row, months: 1, saving: false });

  // 手机端卡片视图:每张令牌一张卡,信息分组展示,底部整行操作按钮(不再有 fixed 列遮挡)
  const renderMobileCards = () => {
    const PAGE_SIZE = 10;
    const totalPage = Math.max(1, Math.ceil(visibleList.length / PAGE_SIZE));
    const page = Math.min(mobilePage, totalPage);
    const pageRows = visibleList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const rowStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4e5969' };
    const cell = (label, value, color) => React.createElement('div', { style: { ...rowStyle, justifyContent: 'space-between' } },
      React.createElement('span', { style: { color: '#86909c', flexShrink: 0 } }, label),
      React.createElement('span', { style: { color: color || '#1f2329', textAlign: 'right' } }, value)
    );
    return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
      pageRows.map((row) => {
        const exp = row.effective_expires_at;
        let expText = '永久';
        let expColor = '#1f2329';
        if (row.status === 5 && (!row.activated_at || row.activated_at === 0)) {
          expText = '—'; expColor = '#86909c';
        } else if (exp && exp > 0) {
          const d = new Date(exp);
          if (!isNaN(d.getTime())) {
            const remaining = exp - Date.now();
            expText = d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
            expColor = remaining < 0 ? '#f56565' : (remaining < 7 * 86400000 ? '#ff7d00' : (remaining < 30 * 86400000 ? '#86909c' : '#1f2329'));
          }
        }
        const checked = selectedRowKeys.includes(row.id);
        return React.createElement('div', {
          key: row.id,
          style: {
            border: '1px solid #e5e6eb', borderRadius: 8, padding: '10px 12px',
            display: 'flex', flexDirection: 'column', gap: 8, background: '#fff',
          },
        },
          // 第一行:勾选框 + ID + 状态
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement(Checkbox, {
              checked,
              // ⚠️ Semi 的 Checkbox onChange 传的是**事件对象**(新状态在 e.target.checked),
              // 不是布尔值。之前写成 (v) => v ? ... : ...,v 恒为真 →
              // 勾上以后再也取消不掉(批量操作只能加不能减)。
              onChange: (e) => {
                const on = !!(e && e.target && e.target.checked);
                setSelectedRowKeys((prev) => (on ? prev.concat(row.id) : prev.filter((k) => k !== row.id)));
              },
            }),
            React.createElement('span', { style: { fontSize: 12, color: '#86909c' } }, '#' + row.id),
            React.createElement('span', { style: { flex: 1 } }),
            React.createElement('span', { style: { color: STATUS_COLORS[row.status_text] || '#c9cdd4', fontSize: 12, fontWeight: 500 } }, row.status_text || '—')
          ),
          // Key 行(整行可点进详情)
          React.createElement('div', {
            style: { display: 'flex', alignItems: 'center', gap: 6 },
            onClick: () => navigate('/admin/tokens/' + row.id),
          },
            React.createElement('code', { style: { fontSize: 12, color: '#1d7dfa', wordBreak: 'break-all' } }, 'sk-' + maskKey(row.key)),
            React.createElement(Button, {
              size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px', fontSize: 11, flexShrink: 0 },
              onClick: (e) => {
                e.stopPropagation();
                copyKey('sk-' + row.key, '已复制完整 Key');
              },
            }, '复制')
          ),
          // 信息区:两列网格
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' } },
            cell('套餐', row.plan_name || (row.plan_id ? '(套餐已删除)' : '未关联'), row.plan_id ? '#1f2329' : '#f56565'),
            cell('5h 限额', React.createElement('span', null,
              React.createElement('span', { style: { color: '#1d7dfa' } }, formatQuota(row.calls_5h || 0)),
              ' / ',
              React.createElement('span', null, row.effective_limit_5h ? formatQuota(row.effective_limit_5h) : '∞'))),
            cell('速度', React.createElement(SpeedCell, { th: row.throttle, live: row.live })),
            cell('周限额', React.createElement('span', null,
              React.createElement('span', { style: { color: '#00b42a' } }, formatQuota(row.calls_week || 0)),
              ' / ',
              React.createElement('span', null, row.effective_limit_week ? formatQuota(row.effective_limit_week) : '∞'))),
            cell('月限额', React.createElement('span', null,
              React.createElement('span', { style: { color: '#8957e5' } }, formatQuota(row.used_quota || 0)),
              ' / ',
              React.createElement('span', null, row.unlimited ? '∞' : formatQuota((row.remain_quota || 0) + (row.used_quota || 0))))),
            cell('说明', React.createElement('span', { style: { color: '#86909c', fontSize: 12 } },
              row.plan_total_calls && (row.remain_quota || 0) + (row.used_quota || 0) > row.plan_total_calls
                ? '已购 ' + formatQuota((row.remain_quota || 0) + (row.used_quota || 0)) + ' 额度(套餐现为 ' + formatQuota(row.plan_total_calls) + ',已售权益不缩水)'
                : '—')),
            cell('过期', expText, expColor)
          ),
          // 备注
          row.remark ? React.createElement('div', { style: { ...rowStyle, justifyContent: 'space-between', alignItems: 'flex-start' } },
            React.createElement('span', { style: { color: '#86909c', flexShrink: 0 } }, '备注'),
            React.createElement('span', { style: { color: '#4e5969', textAlign: 'right', wordBreak: 'break-all' } }, row.remark)
          ) : null,
          // 操作区:整行铺开,不再被 fixed 列遮挡
          React.createElement('div', {
            style: { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderTop: '1px solid #f2f3f5', paddingTop: 8 },
            onClick: (e) => e.stopPropagation(),
          }, renderActions(row))
        );
      }),
      visibleList.length > PAGE_SIZE ? React.createElement(Pagination, {
        total: visibleList.length, pageSize: PAGE_SIZE, currentPage: page,
        onPageChange: (p) => setMobilePage(p),
        size: 'small', style: { alignSelf: 'center' },
      }) : null
    );
  };

  // 操作按钮组:表格「操作」列与手机卡片视图共用
  const renderActions = (row) => {
    // 待售(status=5 且 sold_status=0)的"启用/禁用"没有意义——只能等买家首次调用自动激活。
    // 这个位置直接换成「发货」:一键标记售出,免去"勾选 → 顶部批量标记"的流程
    const isPendingSale = row.status === 5 && row.sold_status === 0;
    const onToggle = (e) => {
      e.stopPropagation();
      toggleStatus(row);
    };
    return React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' } },
      React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px' }, onClick: (e) => { e.stopPropagation(); openEdit(row); } }, '编辑'),
      React.createElement('span', { style: { color: '#c9cdd4', userSelect: 'none' } }, '|'),
      React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px' }, onClick: (e) => { e.stopPropagation(); openRenew(row); } }, '续期'),
      React.createElement('span', { style: { color: '#c9cdd4', userSelect: 'none' } }, '|'),
      isPendingSale
        ? React.createElement(Button, {
            size: 'small', theme: 'borderless', type: 'warning',
            style: { padding: '0 6px', color: '#ff7d00', fontWeight: 600 },
            onClick: (e) => {
              e.stopPropagation();
              setMarkDialog({ open: true, row, soldTo: '', saving: false });
            },
          }, '发货')
        : React.createElement(Button, { size: 'small', theme: 'borderless', type: 'primary', style: { padding: '0 6px' }, onClick: onToggle }, row.status === 1 ? '禁用' : '启用'),
      React.createElement('span', { style: { color: '#c9cdd4', userSelect: 'none' } }, '|'),
      React.createElement(Button, { size: 'small', theme: 'borderless', type: 'danger', style: { padding: '0 6px' }, onClick: (e) => { e.stopPropagation(); remove(row); } }, '删除')
    );
  };

  const columns = [
    // 直接显示内部 ID。
    // 提醒:ID 是 PostgreSQL 的 SERIAL 自增主键,行删掉后号不回收,所以历史删除越多 ID 越跳。
    // 这是数据库的正常行为,不是数据坏了;改代码让旧号复用反而会让历史日志指向别的令牌。
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: 'Key', dataIndex: 'key', width: 260,
      render: (k, row) => React.createElement('div', {
        style: { display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
        onClick: (e) => {
          e.stopPropagation();
          navigate('/admin/tokens/' + row.id);
        },
      },
        React.createElement('code', {
          style: { fontSize: 12, color: '#1d7dfa', whiteSpace: 'nowrap', background: 'transparent', cursor: 'pointer' },
          title: '点击查看日志',
        }, 'sk-' + maskKey(k)),
        React.createElement(Button, {
          size: 'small', theme: 'borderless', type: 'primary',
          style: { padding: '0 8px', fontSize: 11 },
          onClick: (e) => {
            e.stopPropagation();
            copyKey('sk-' + k, '已复制完整 Key');
          },
        }, '复制'),
      ),
    },
    {
      title: '状态', dataIndex: 'status_text', width: 75,
      render: (text) => {
        // 后端 SQL 投影出来的业务状态文案:待售/待激活/使用中/已禁用/已过期/已耗尽
        return React.createElement('span', { style: { color: STATUS_COLORS[text] || '#c9cdd4', whiteSpace: 'nowrap' } }, text || '—');
      },
    },
    {
      title: '套餐', dataIndex: 'plan_name', width: 120,
      render: (name, row) => React.createElement('div', { style: { fontSize: 12, lineHeight: 1.4 } },
        React.createElement('div', {
          style: { color: row.plan_id ? '#1f2329' : '#f56565', fontWeight: 500 },
        }, name || (row.plan_id ? '(套餐已删除)' : '未关联')),
        row.plan_enabled === 0 && React.createElement('div', { style: { fontSize: 11, color: '#ff7d00' } }, '套餐已下架'),
      ),
    },
    {
      title: '5h 限额', width: 95,
      render: (_, row) => React.createElement('span', { style: { color: '#52525b', whiteSpace: 'nowrap' }, title: '单位:额度,不是次数(M3 约 3 额度/次、Flash 约 2 额度/次)' },
        React.createElement('span', { style: { color: '#1d7dfa' } }, formatQuota(row.calls_5h || 0)),
        ' / ',
        React.createElement('span', null, row.effective_limit_5h ? formatQuota(row.effective_limit_5h) : '∞')
      ),
    },
    {
      // 该卡**此刻在飞**请求的实时输出速度(t/s)。
      // 没有在飞请求就显示「—」—— 有值的行就是正在被调用的卡。
      title: '速度', width: 82,
      render: (_, row) => React.createElement(SpeedCell, { th: row.throttle, live: row.live }),
    },
    {
      title: '周限额', width: 95,
      render: (_, row) => React.createElement('span', { style: { color: '#52525b', whiteSpace: 'nowrap' }, title: '单位:额度,不是次数(M3 约 3 额度/次、Flash 约 2 额度/次)' },
        React.createElement('span', { style: { color: '#00b42a' } }, formatQuota(row.calls_week || 0)),
        ' / ',
        React.createElement('span', null, row.effective_limit_week ? formatQuota(row.effective_limit_week) : '∞')
      ),
    },
    {
      // 一个计费周期(套餐天数,通常按月理解)内的总额度限额。
      // 格式与左侧 5h / 周限额保持一致:已用 / 限额,无限令牌限额位显示 ∞。
      // used_quota 会被 rollPeriod 在周期结束时清零,所以它天然就是"本周期已用"。
      //
      // ⚠ 单位是**额度**不是次数:一次调用按模型倍率扣额度(M3=3、Flash=2),
      //   耗时跨 60 秒还会按分钟段再乘。套餐页现在也统一标"额度"了。
      // ⚠ 这个数可能大于套餐当前的月配额:套餐降额时已售卡不缩水(已付权益),
      //   hover 会说明差在哪。
      title: '月限额',
      width: 105,
      render: (_, row) => {
        const total = (row.remain_quota || 0) + (row.used_quota || 0);
        const planTotal = row.plan_total_calls || 0;
        const tip = planTotal && total > planTotal
          ? '已购 ' + formatQuota(total) + ' 额度(套餐现为 ' + formatQuota(planTotal) + ',已售权益不缩水)\n'
            + '单位:额度,不是次数(M3 约 3 额度/次、Flash 约 2 额度/次)'
          : '单位:额度,不是次数(M3 约 3 额度/次、Flash 约 2 额度/次)';
        return React.createElement('span', { style: { color: '#52525b', whiteSpace: 'nowrap' }, title: tip },
          React.createElement('span', { style: { color: '#8957e5' } }, formatQuota(row.used_quota || 0)),
          ' / ',
          // 无限令牌的 remain_quota 存的是 -1,不能参与求和
          React.createElement('span', null, row.unlimited ? '∞' : formatQuota(total))
        );
      },
    },
    {
      title: '过期', width: 140,
      render: (_, row) => {
        if (row.status === 5 && (!row.activated_at || row.activated_at === 0)) {
          return React.createElement('span', { style: { color: '#86909c' } }, '—');
        }
        const exp = row.effective_expires_at;
        if (!exp || exp <= 0) {
          return React.createElement('span', { style: { color: '#86909c' } }, '永久');
        }
        const d = new Date(exp);
        if (isNaN(d.getTime())) return React.createElement('span', null, '—');
        const now = Date.now();
        const remaining = exp - now;
        // 颜色规则:已过期 → 红;< 7 天 → 橙;< 30 天 → 灰;其余 → 黑
        const color = remaining < 0
          ? '#f56565'
          : (remaining < 7 * 86400000 ? '#ff7d00'
          : (remaining < 30 * 86400000 ? '#86909c' : '#1f2329'));
        return React.createElement('span', { style: { color, whiteSpace: 'nowrap' } },
          d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
        );
      },
    },
    {
      title: '备注', dataIndex: 'remark', width: 180,
      render: (r) => {
        if (!r) return React.createElement('span', { style: { color: '#c9cdd4' } }, '—');
        // 内容过长时省略号截断(保留 title 看完整内容),不换行
        return React.createElement('span', {
          title: r,
          style: {
            display: 'inline-block', maxWidth: 160,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            verticalAlign: 'middle', color: '#4e5969',
          },
        }, r);
      },
    },
    {
      title: '操作', width: 210, fixed: 'right',
      render: (_, row) => renderActions(row),
    },
  ];

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const showNewKey = isCreate && newKeyResult;

  // 编辑态的套餐下拉:
  //   1) 「未关联套餐」(plan_id=0)必须能选 —— 令牌本来就允许不绑套餐,
  //      以前下拉框里没这个选项,状态显示不出来,保存还会被后端拒。
  //   2) 绑定的套餐已被删除时,补一个占位选项,否则 Select 显示空白,
  //      一保存就报「套餐不存在」,改备注都改不了。
  const editingRow = isEdit ? list.find((r) => r.id === editingId) : null;
  const orphanPlanId = editingRow?.plan_id && !plans.some((p) => p.id === editingRow.plan_id)
    ? String(editingRow.plan_id) : null;

  const selectedCount = selectedRowKeys.length;
  const hasSelection = selectedCount > 0;

  return React.createElement('div', null,
    // 标题行(左: 令牌管理 / 右: + 生成令牌)
    React.createElement('div', { style: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 16, gap: 8, flexWrap: 'wrap',
    } },
      React.createElement('h2', { style: { fontSize: 18, fontWeight: 600, margin: 0 } }, '令牌管理'),
      React.createElement(Button, { type: 'primary', theme: 'solid', onClick: openNew }, '+ 生成令牌'),
    ),

    // 筛选条件(左) + 操作按钮(右)
    React.createElement('div', { style: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 8, marginBottom: 12, flexWrap: 'wrap',
    } },
      // 左:筛选区
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
        // 搜索框:按 Key / 备注 / ID / 用户名 / 售出对象 / 套餐名 过滤
        React.createElement(Input, {
          size: 'small',
          style: { width: isMobile ? 160 : 240 },
          value: search,
          onChange: (v) => setSearch(v || ''),
          prefix: React.createElement(IconSearch, { size: 'small' }),
          showClear: true,
          placeholder: '搜索 Key / 备注 / ID / 用户',
          'aria-label': '搜索令牌',
        }),
        React.createElement(Select, {
          size: 'small', style: { width: isMobile ? 110 : 140 },
          value: soldFilter,
          onChange: (v) => { setSoldFilter(v); setMobilePage(1); setTablePage(1); },
          optionList: [
            { value: '', label: '全部状态' },
            { value: '0', label: '未售出' },
            { value: '1', label: '已售出' },
          ],
        }),
        React.createElement(Select, {
          size: 'small', style: { width: isMobile ? 130 : 200 },
          value: planFilter,
          onChange: (v) => { setPlanFilter(v); setMobilePage(1); setTablePage(1); },
          optionList: [
            { value: '', label: '全部套餐' },
            { value: '0', label: '未关联套餐' },
            ...plans.map((p) => ({ value: String(p.id), label: p.name + (p.enabled ? '' : ' (已下架)') })),
          ],
        }),
        React.createElement('span', {
          title: '每 5 秒自动刷新列表', style: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#86909c' },
        },
          React.createElement(Switch, { size: 'small', checked: autoRefresh, onChange: (v) => setAutoRefresh(v) }),
          '自动刷新',
        ),
        React.createElement('span', {
          title: '打开后列表不显示状态为「已过期」的令牌(只隐藏显示,不影响数据)',
          style: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#86909c' },
        },
          React.createElement(Switch, {
            size: 'small', checked: hideExpired,
            onChange: (v) => { setHideExpired(v); setTablePage(1); setMobilePage(1); },
          }),
          '隐藏过期',
        ),
        hideExpired && hiddenExpiredCount > 0
          ? React.createElement('span', { style: { fontSize: 12, color: '#86909c' } }, `已隐藏 ${hiddenExpiredCount} 个`)
          : null,
        searchKey
          ? React.createElement('span', { style: { fontSize: 12, color: '#1d7dfa' } },
              `筛出 ${searchHitCount} / ${list.length} 条`)
          : null,
        hasSelection ? React.createElement('span', { style: { fontSize: 12, color: '#1d7dfa', marginLeft: 4 } }, `已选 ${selectedCount} 项`) : null,
      ),
      // 右:操作区
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
        hasSelection ? React.createElement(Button, {
          size: 'small', type: 'tertiary',
          onClick: () => setBatchDeleteDialog({ open: true, ids: selectedRowKeys.slice(), saving: false }),
        }, `批量删除(${selectedCount})`) : null,
        hasSelection ? React.createElement(Button, {
          size: 'small', type: 'tertiary',
          onClick: () => window.open('/api/admin/tokens/export?ids=' + encodeURIComponent(selectedRowKeys.join(',')), '_blank'),
        }, `导出CSV(${selectedCount})`) : null,
      ),
    ),
    React.createElement('div', { style: { background: '#fff', borderRadius: 10, padding: 8, border: '1px solid #e5e6eb' } },
      React.createElement(Spin, { spinning: loading },
        isMobile
          // 手机端:卡片视图。表格 11 列 + fixed 操作列在窄屏会互相遮挡、Key 被压没
          ? renderMobileCards()
          : React.createElement(Table, {
              size: 'small', rowKey: 'id', columns, dataSource: pagedList,
              scroll: { x: 1360 },
              rowSelection: {
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                // 全部行可勾选:已售出的卡也需要批量删除/批量导出
              },
              onRow: (record) => ({
                onClick: (e) => {
                  // 复选框 / 操作列 / 复制按钮 已在内部 stopPropagation,这里兜底再判一次 target
                  const tag = (e.target && e.target.tagName) || '';
                  if (tag === 'INPUT' || tag === 'BUTTON' || tag === 'LABEL') return;
                  navigate('/admin/tokens/' + record.id);
                },
                style: { cursor: 'pointer' },
              }),
              pagination: {
                currentPage: tablePage,
                pageSize: TABLE_PAGE_SIZE,
                total: visibleList.length,
                hideOnSinglePage: true,
                onPageChange: setTablePage,
              },
            })
      )
    ),
    React.createElement(Modal, {
      title: isCreate ? '生成新令牌' : '编辑令牌',
      visible: modalOpen,
      onCancel: () => setModalOpen(false),
      width: 600,
      footer: showNewKey
        ? React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' } },
            React.createElement(Button, { onClick: () => setModalOpen(false) }, '关闭'),
            newKeyResult.count > 1
              ? React.createElement(Button, { type: 'primary', theme: 'solid', onClick: () => {
                  const all = newKeyResult.keys.map((k) => k.full).join('\n');
                  copyKey(all, '已复制全部 ' + newKeyResult.count + ' 个');
                }}, '复制全部 Key')
              : React.createElement(Button, { type: 'primary', theme: 'solid', onClick: () => {
                  copyKey(newKeyResult.keys?.[0]?.full || newKeyResult.key, '已复制');
                }}, '复制 Key')
          )
        : React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 16, borderTop: '1px solid #e5e7eb' } },
            React.createElement(Button, { onClick: () => setModalOpen(false) }, '取消'),
            React.createElement(Button, { type: 'primary', theme: 'solid', onClick: save, loading: saving }, isCreate ? '生成' : '保存'),
          ),
    },
      showNewKey
        ? React.createElement('div', null,
            newKeyResult.count > 1
              ? React.createElement('div', null,
                  React.createElement('div', { style: { marginBottom: 12, fontSize: 13, color: '#4e5969' } }, '✓ 已生成 ' + newKeyResult.count + ' 个 Key(完整 Key 只显示一次,请立即复制!)'),
                  React.createElement('textarea', {
                    readOnly: true,
                    rows: Math.min(12, newKeyResult.count + 2),
                    style: { width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 12, padding: 10, border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6', resize: 'vertical', color: '#ad6800', fontWeight: 600 },
                    value: newKeyResult.keys.map((k) => k.full).join('\n'),
                    onClick: function(e){ e.target.select(); copyKey(e.target.value, '已全部复制'); },
                  }),
                  React.createElement('div', { style: { fontSize: 12, color: '#86909c', marginTop: 6 } }, '点击文本框可一键复制全部 Key')
                )
              : React.createElement('div', null,
                  React.createElement('div', { style: { marginBottom: 12, fontSize: 13, color: '#4e5969' } }, '令牌已创建成功,完整 Key 只显示这一次,请立即复制保存!'),
                  React.createElement('div', { style: { background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 14, marginBottom: 14 } },
                    React.createElement('code', { style: { color: '#ad6800', fontSize: 13, fontWeight: 600, wordBreak: 'break-all', fontFamily: 'ui-monospace, Menlo, monospace', background: 'transparent' } }, (newKeyResult.keys && newKeyResult.keys[0] && newKeyResult.keys[0].full) || newKeyResult.key)
                  )
                )
          )
        : React.createElement('div', null,
            // 生成数量(仅创建模式)
            isCreate && React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12, background: '#fafbfc', padding: 10, borderRadius: 6, border: '1px solid #e5e6eb' } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, '生成数量'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement(InputNumber, {
                  min: 1, max: 500, step: 10,
                  value: Number(form.count) || 1,
                  onChange: (v) => setForm({ ...form, count: v }),
                  style: { width: 140 },
                }),
                React.createElement('span', { style: { fontSize: 12, color: '#86909c', marginLeft: 8 } }, form.count > 1 ? '将一次生成 ' + form.count + ' 个相同配置的 Key(默认未售出)' : '= 单次生成 1 个'),
              )
            ),
                        plans.length > 0 && React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12, background: '#f4f8ff', padding: 10, borderRadius: 6, border: '1px solid #dbe8ff' } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#1d7dfa', paddingTop: 6, flexShrink: 0, fontWeight: 500 } }, '所属套餐 *'),
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement(Select, {
                  style: { width: '100%', minWidth: 0 },
                  value: selectedPlanId,
                  onChange: applyPlan,
                  placeholder: isCreate ? '必选,令牌的所有限额/过期/模型限制都从套餐读取' : '未关联套餐',
                  optionList: [
                    // 编辑模式:允许显式选「未关联套餐」(plan_id=0)或保留已删除的套餐
                    ...(isEdit ? [{ value: '0', label: '未关联套餐' }] : []),
                    ...(orphanPlanId ? [{ value: orphanPlanId, label: '(套餐已删除)' }] : []),
                    ...plans.filter((p) => isCreate ? p.enabled : true).map((p) => ({
                      value: String(p.id),
                      label: `${p.name}${p.enabled ? '' : ' (已下架)'} · ￥${(p.price_cents / 100).toFixed(2)}/月 · ${p.total_calls} 额度 · 5h ${p.limit_5h || '∞'} · ${p.expired_days > 0 ? p.expired_days + '天' : '永久'}`,
                    })),
                  ],
                }),
                React.createElement('div', {
                  className: 'form-hint',
                  title: '选套餐后,默认余额 = 套餐额度。修改套餐,所有令牌自动跟随(过期时间、5h/周限额、模型限制、套餐额度)',
                }, '选套餐后默认余额 = 套餐额度,改套餐所有令牌自动跟随'),
              )
            ),
            React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12 } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, '额度余额'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
                  React.createElement(InputNumber, {
                    disabled: form.unlimited, min: 0, step: 1000,
                    value: Number(form.remain_quota) || 0,
                    onChange: (v) => setForm({ ...form, remain_quota: v }),
                    style: { flex: 1 },
                  }),
                  React.createElement(Switch, { checked: form.unlimited, onChange: (v) => setForm({ ...form, unlimited: v }) }),
                  React.createElement('span', { style: { fontSize: 12, color: '#86909c' } }, '无限'),
                ),
                React.createElement('div', {
                  className: 'form-hint',
                  title: '每次成功调用按模型倍率扣费(最低 1,无限时不扣),0 = 套餐用尽。选套餐后默认 = 套餐额度。输入 token 超过渠道配置的上下文窗口时按溢价倍率扣费',
                }, '按模型倍率扣费(最低 1,无限时不扣),0 = 套餐用尽'),
              )
            ),
            isEdit && React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12 } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, 'API Key'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement(Input, {
                  value: form.key,
                  onChange: (v) => setForm({ ...form, key: v }),
                  placeholder: 'sk- 前缀可选,保存时自动去掉',
                  mode: 'password',
                }),
                React.createElement('div', { className: 'form-hint' }, '改了 Key 后,旧 Key 立即失效,需要把新 Key 给调用方')
              )
            ),
            React.createElement('div', { className: 'form-row', style: { display: 'flex', alignItems: 'flex-start', marginBottom: 12 } },
              React.createElement('label', { style: { width: 110, fontSize: 13, color: '#4e5969', paddingTop: 6, flexShrink: 0 } }, '备注'),
              React.createElement('div', { style: { flex: 1 } },
                React.createElement(Input, {
                  value: form.remark,
                  onChange: (v) => setForm({ ...form, remark: v }),
                  placeholder: '可选,例如 alice-2025-09、闲鱼买家张三',
                })
              )
            )
          )
    ),

    // 行内「发货」弹窗:一键标记售出,买家/订单号可选写入备注列。
    // 只做标记售出,没有取消售出功能——发错卡直接删除令牌
    markDialog && markDialog.open ? React.createElement(Modal, {
      key: 'mark-dialog',
      title: '发货',
      visible: markDialog.open,
      onCancel: () => setMarkDialog(null),
      width: 440,
      okText: '确认发货',
      okButtonProps: { loading: !!markDialog.saving },
      cancelText: '关闭',
      onOk: async () => {
        setMarkDialog((d) => d ? Object.assign({}, d, { saving: true }) : d);
        const id = markDialog.row.id;
        try {
          await API.post('/api/admin/tokens/mark-sold', {
            ids: [id],
            sold_to: markDialog.soldTo || '',
          });
          Toast.success('已发货(标记为已售出)');
          setMarkDialog(null);
          setSelectedRowKeys([]);
          load();
        } catch (e) {
          Toast.error('操作失败');
          setMarkDialog((d) => d ? Object.assign({}, d, { saving: false }) : d);
        }
      },
    },
      React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 13, color: '#1d2129', marginBottom: 12 } },
              '发货后标记为已售出,状态变为「待激活」,买家首次调用后自动激活'
            ),
            React.createElement('div', { style: { marginBottom: 8, fontSize: 13, color: '#4e5969' } }, '买家/订单号(可选,自动写入备注列)'),
            React.createElement(Input, {
              value: markDialog.soldTo || '',
              onChange: (v) => setMarkDialog((d) => d ? Object.assign({}, d, { soldTo: v }) : d),
              placeholder: '例如:小鱼儿 / 订单 20250916-001',
              maxLength: 64,
              showClear: true,
            })
          )
    ) : null,

    // 批量删除确认弹窗
    batchDeleteDialog && batchDeleteDialog.open ? React.createElement(Modal, {
      key: 'batch-delete-dialog',
      title: '确认批量删除',
      visible: batchDeleteDialog.open,
      onCancel: () => setBatchDeleteDialog(null),
      width: 440,
      okText: '确认删除',
      okButtonProps: { loading: !!batchDeleteDialog.saving, type: 'danger' },
      cancelText: '取消',
      onOk: async () => {
        setBatchDeleteDialog((d) => d ? Object.assign({}, d, { saving: true }) : d);
        try {
          const r = await API.post('/api/admin/tokens/batch-delete', { ids: batchDeleteDialog.ids });
          Toast.success('已删除 ' + (r.data?.data?.deleted ?? batchDeleteDialog.ids.length) + ' 个令牌');
          setBatchDeleteDialog(null);
          setSelectedRowKeys([]);
          load();
        } catch (e) {
          Toast.error(e.response?.data?.message || '删除失败');
          setBatchDeleteDialog((d) => d ? Object.assign({}, d, { saving: false }) : d);
        }
      },
    },
      React.createElement('div', { style: { fontSize: 13, color: '#1d2129' } },
        React.createElement('div', { style: { marginBottom: 8 } },
          '将永久删除 ',
          React.createElement('b', { style: { color: '#f56565' } }, batchDeleteDialog.ids.length),
          ' 个令牌及其调用日志,此操作不可恢复。'
        ),
        React.createElement('div', { style: { fontSize: 12, color: '#86909c' } }, '请确认无重要数据后再操作。')
      )
    ) : null,

    // 续期弹窗
    renewDialog && renewDialog.open ? React.createElement(Modal, {
      key: 'renew-dialog',
      title: '令牌续期',
      visible: renewDialog.open,
      onCancel: () => setRenewDialog(null),
      width: 460,
      okText: '确认续期',
      okButtonProps: { loading: !!renewDialog.saving, type: 'primary' },
      cancelText: '取消',
      onOk: doRenew,
    },
      React.createElement('div', { style: { fontSize: 13, color: '#1d2129', lineHeight: 1.9 } },
        React.createElement('div', null,
          '令牌 ',
          React.createElement('code', { style: { fontSize: 12, color: '#1d7dfa' } }, 'sk-' + maskKey(renewDialog.row.key)),
          ' 续期'
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' } },
          React.createElement('span', { style: { fontSize: 13 } }, '续期时长'),
          React.createElement(InputNumber, {
            min: 1, max: 12, defaultValue: renewDialog.months,
            style: { width: 100 },
            onChange: (v) => setRenewDialog((d) => (d ? Object.assign({}, d, { months: v || 1 }) : d)),
          }),
          React.createElement('span', { style: { fontSize: 13 } }, '个月')
        ),
        React.createElement('div', { style: {
          padding: 10, marginTop: 6, background: '#f7f8fa',
          borderRadius: 4, fontSize: 12, color: '#4e5969',
        } },
          React.createElement('div', null, '当前到期：' + (
            renewDialog.row.effective_expires_at > 0
              ? new Date(renewDialog.row.effective_expires_at).toLocaleString('zh-CN')
              : '永久/未计时'
          )),
          renewDialog.row.unlimited
            ? React.createElement('div', { style: { marginTop: 4 } }, '额度：无限额度令牌，仅延长有效期')
            : React.createElement('div', { style: { marginTop: 4 } },
                '当前周期用量（保持不变）：' + formatQuota(renewDialog.row.used_quota) + ' / ' +
                formatQuota((renewDialog.row.remain_quota || 0) + (renewDialog.row.used_quota || 0))
              ),
          React.createElement('div', { style: { marginTop: 6, color: '#86909c' } },
            '续期只延长总有效期，不影响本周期用量。到期进入新周期时额度自动清零重新计费。'
          ),
        )
      )
    ) : null
  );
};

export default AdminTokens;
